import {
  CHAT_HISTORY_LIMIT,
  CHAT_MESSAGE_MAX_BYTES,
  CHAT_MESSAGE_MAX_CHARACTERS,
  type ChatAuthor,
  type ChatMessage,
  type ChatSettings,
} from './ChatTypes';

export type ChatClientEnvelope =
  | { readonly type: 'auth'; readonly token: string }
  | { readonly type: 'send'; readonly clientMessageId: string; readonly text: string };

export type ChatServerEnvelope =
  | { readonly type: 'ready'; readonly channelId: string; readonly history: readonly ChatMessage[]; readonly settings: ChatSettings }
  | { readonly type: 'history'; readonly messages: readonly ChatMessage[] }
  | { readonly type: 'message'; readonly message: ChatMessage }
  | { readonly type: 'ack'; readonly clientMessageId: string; readonly messageId: string }
  | { readonly type: 'settings'; readonly settings: ChatSettings }
  | { readonly type: 'error'; readonly code: string; readonly message: string; readonly clientMessageId?: string };

const MAX_SERVER_ENVELOPE_BYTES = 64 * 1024;

export function encodeChatClientEnvelope(envelope: ChatClientEnvelope): ArrayBuffer {
  return new TextEncoder().encode(JSON.stringify(envelope)).buffer;
}

export function decodeChatServerEnvelope(payload: ArrayBuffer): ChatServerEnvelope {
  if (payload.byteLength === 0 || payload.byteLength > MAX_SERVER_ENVELOPE_BYTES) {
    throw new Error('Invalid chat server envelope size.');
  }
  let parsed: unknown;
  try { parsed = JSON.parse(new TextDecoder().decode(payload)); }
  catch { throw new Error('Invalid chat server JSON.'); }
  if (!isRecord(parsed) || typeof parsed.type !== 'string') throw new Error('Invalid chat server envelope.');

  switch (parsed.type) {
    case 'ready': {
      if (typeof parsed.channelId !== 'string' || !Array.isArray(parsed.history)
        || parsed.history.length > CHAT_HISTORY_LIMIT || !isChatSettings(parsed.settings)) {
        throw new Error('Invalid chat ready envelope.');
      }
      return {
        type: 'ready', channelId: parsed.channelId,
        history: parsed.history.map(parseChatMessage), settings: parsed.settings,
      };
    }
    case 'history':
      if (!Array.isArray(parsed.messages) || parsed.messages.length > CHAT_HISTORY_LIMIT) {
        throw new Error('Invalid chat history envelope.');
      }
      return { type: 'history', messages: parsed.messages.map(parseChatMessage) };
    case 'message':
      return { type: 'message', message: parseChatMessage(parsed.message) };
    case 'ack':
      if (!validId(parsed.clientMessageId) || !validId(parsed.messageId)) throw new Error('Invalid chat acknowledgement.');
      return { type: 'ack', clientMessageId: parsed.clientMessageId, messageId: parsed.messageId };
    case 'settings':
      if (!isChatSettings(parsed.settings)) throw new Error('Invalid chat settings envelope.');
      return { type: 'settings', settings: parsed.settings };
    case 'error': {
      if (typeof parsed.code !== 'string' || !parsed.code || typeof parsed.message !== 'string'
        || (parsed.clientMessageId !== undefined && !validId(parsed.clientMessageId))) {
        throw new Error('Invalid chat error envelope.');
      }
      return {
        type: 'error', code: parsed.code, message: parsed.message,
        ...(parsed.clientMessageId === undefined ? {} : { clientMessageId: parsed.clientMessageId }),
      };
    }
    default:
      throw new Error('Unknown chat server envelope.');
  }
}

export function isChatSettings(value: unknown): value is ChatSettings {
  return isRecord(value)
    && typeof value.globalEnabled === 'boolean'
    && typeof value.guestGlobalEnabled === 'boolean'
    && typeof value.guestMatchEnabled === 'boolean';
}

export function parseChatMessage(value: unknown): ChatMessage {
  if (!isRecord(value) || !validId(value.id) || !Number.isSafeInteger(value.sequence) || (value.sequence as number) < 0
    || !validId(value.channelId) || (value.kind !== 'message' && value.kind !== 'system')
    || (value.author !== null && !isChatAuthor(value.author)) || typeof value.text !== 'string'
    || Array.from(value.text).length > CHAT_MESSAGE_MAX_CHARACTERS
    || new TextEncoder().encode(value.text).length > CHAT_MESSAGE_MAX_BYTES
    || typeof value.createdAt !== 'number' || !Number.isFinite(value.createdAt)
    || (value.clientMessageId !== undefined && !validId(value.clientMessageId))) {
    throw new Error('Invalid chat message.');
  }
  return value as unknown as ChatMessage;
}

function isChatAuthor(value: unknown): value is ChatAuthor {
  return isRecord(value) && validId(value.id) && typeof value.displayName === 'string'
    && (value.kind === 'guest' || value.kind === 'account' || value.kind === 'bot');
}

function validId(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 256;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
